import { openDB, type IDBPDatabase } from 'idb';
import type { IProgressBatchResponse, IProgressEvent } from '@stem/contracts';
import { apiClient, ApiError } from '../services/api';

const DB_NAME = 'stem-offline';
const STORE = 'progress-events';
const VERSION = 1;

/**
 * The offline progress queue.
 *
 * Every learner action is written here first and sent second. Not "written here
 * when offline" — *always*, and that is the important part. A queue that only
 * engages when `navigator.onLine` is false still loses the event that was
 * in flight when the signal dropped, and on a 2G connection in a rural school
 * that is most of them.
 *
 * The server side of the contract is what makes this safe: events carry a
 * client-generated UUIDv7 primary key and `POST /learn/events` upserts on it,
 * so re-sending a batch that partly succeeded writes nothing twice. The queue
 * therefore never has to know what got through — it can resend the lot and
 * delete on confirmation.
 */
class OfflineQueue {
  private db: Promise<IDBPDatabase> | null = null;
  private flushing = false;
  private listeners = new Set<(pending: number) => void>();

  private open(): Promise<IDBPDatabase> {
    if (!this.db) {
      this.db = openDB(DB_NAME, VERSION, {
        upgrade(database) {
          const store = database.createObjectStore(STORE, { keyPath: 'id' });
          store.createIndex('occurredAt', 'occurredAt');
        },
      });
    }
    return this.db;
  }

  /** Writes an event and tries to send. The write never fails on a bad network. */
  async enqueue(event: IProgressEvent): Promise<void> {
    const db = await this.open();
    await db.put(STORE, event);
    this.notify();
    void this.flush();
  }

  async enqueueAll(events: IProgressEvent[]): Promise<void> {
    if (!events.length) return;
    const db = await this.open();
    const tx = db.transaction(STORE, 'readwrite');
    await Promise.all(events.map(event => tx.store.put(event)));
    await tx.done;
    this.notify();
    void this.flush();
  }

  async pending(): Promise<number> {
    const db = await this.open();
    return db.count(STORE);
  }

  /**
   * Sends everything queued, in batches.
   *
   * Rows are deleted only after the server has acknowledged them — including
   * the ones it reports as duplicates, because a duplicate means it already has
   * that row and holding on to it forever would be a queue that never empties.
   *
   * A rejected event is also deleted, and this is a real decision rather than
   * an oversight: a malformed event will be malformed on every retry, so
   * keeping it turns one bad row into a queue that fails on every flush and
   * blocks every good row behind it.
   */
  async flush(): Promise<{ sent: number; failed: boolean }> {
    if (this.flushing) return { sent: 0, failed: false };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return { sent: 0, failed: true };
    }
    if (!apiClient.hasOrgToken()) return { sent: 0, failed: true };

    this.flushing = true;
    let sent = 0;

    try {
      const db = await this.open();

      // Batches of 100 rather than everything at once. A learner who worked
      // offline for an hour can have hundreds of events, and one enormous
      // request on a reconnecting phone is the request most likely to time out.
      for (;;) {
        const events = (await db.getAll(STORE, undefined, 100)) as IProgressEvent[];
        if (!events.length) break;

        const result = await apiClient.post<IProgressBatchResponse>('/v1/learn/events', {
          events,
        });

        const tx = db.transaction(STORE, 'readwrite');
        await Promise.all(events.map(event => tx.store.delete(event.id)));
        await tx.done;

        sent += result.accepted;
        this.notify();

        if (events.length < 100) break;
      }

      return { sent, failed: false };
    } catch (error) {
      // A network failure leaves the rows in place — that is the whole point.
      // A 4xx that is not about the network is logged and also left, because
      // the alternative is discarding a learner's work on a server bug.
      if (!(error instanceof ApiError) || error.status === 0) {
        return { sent, failed: true };
      }
      console.warn('Progress flush rejected:', error.message);
      return { sent, failed: true };
    } finally {
      this.flushing = false;
    }
  }

  onChange(listener: (pending: number) => void): () => void {
    this.listeners.add(listener);
    void this.pending().then(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    void this.pending().then(count => {
      for (const listener of this.listeners) listener(count);
    });
  }

  /**
   * Flush on reconnect, on tab focus, and on a slow heartbeat.
   *
   * Three triggers because each covers a case the others miss. `online` fires
   * when the OS notices; focus catches the phone that was asleep in a pocket
   * and never fired an event; and the interval catches a captive-portal wifi
   * that reports online while dropping every packet.
   */
  start(): () => void {
    const attempt = () => void this.flush();

    window.addEventListener('online', attempt);
    window.addEventListener('focus', attempt);
    const timer = window.setInterval(attempt, 60_000);
    attempt();

    return () => {
      window.removeEventListener('online', attempt);
      window.removeEventListener('focus', attempt);
      window.clearInterval(timer);
    };
  }
}

export const offlineQueue = new OfflineQueue();
